import { Box, Typography, useMediaQuery, Menu, MenuItem, Button, Link } from '@mui/material'
import ArrowDropDownRoundedIcon from '@mui/icons-material/ArrowDropDownRounded'
import { useTheme } from '@mui/material/styles'
import { useEffect, useState } from 'react'
import Header from '../header'
import { useStatsig } from '../../lib/statsigAndGa'
import { shouldStudent } from './constData'
import Search from './search'
import OverTime from './overtime'
import AverageRent from './averageRent'
import LineChart from './lineChart'
import Footer from '../footer'
import RentByNeighborhood from './rentByNeighborhood'
import Avg from './avg'
import ToResearch from '../research'
import MarketPageSeo from '../seo/marketSeo'
import PageBreadcrumbs from '../pageBreadcrumbs'
import MapIcon from './mapIcon'
import { currentCityList, getAreaListFromCity } from './cityArea'

const MarketTrends = ({ isMobileDevice, marketData, filterBy }) => {
  const { city: cityName, area: areaName } = filterBy || {}
  const isStudent = shouldStudent(areaName)
  useStatsig('market_trends', {}, false)

  const { queryRentalMarketTrends = {} } = marketData
  const {
    apartmentAverageRent,
    averageRent,
    marketTrendsOverTime,
    medianRentsTrends,
    rentByNeighborhood
  } = queryRentalMarketTrends || {}
  // const { availableApartmentNums } = averageRent

  const theme = useTheme()
  // eslint-disable-next-line
  const mediaQuery = useMediaQuery(theme?.breakpoints?.up('lg'))
  const [isPC, setIsPC] = useState(!isMobileDevice)
  const [anchor, setAnchor] = useState(null)
  const [dropDownId, setDropDownId] = useState('')

  useEffect(() => {
    setIsPC(mediaQuery)
  }, [mediaQuery])
  const locationName = `${areaName} ${areaName ? ',' : ''} ${cityName}`

  const openArea = Boolean(anchor)
  const finalAreaList = () => {
    const list = getAreaListFromCity(cityName)
    return list.filter(v => !['Palms', 'Century City'].includes(v.name))
  }
  const itemList = dropDownId === 'dropdown-city-selector' ? currentCityList : finalAreaList()
  const getHref = item => {
    const { route } = item
    return `/market-trends/${route}`
  }

  return (
    <>
      <Menu
        id="city-area-selector-drop"
        classes={{
          paper: 'rounded-lg',
          list: 'rounded-lg gap-1 md:gap-2 flex flex-col py-3 md:py-4'
        }}
        style={{}}
        elevation={0}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left'
        }}
        MenuListProps={{
          'aria-labelledby': dropDownId
        }}
        anchorEl={anchor}
        open={openArea}
        onClose={() => setAnchor(null)}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left'
        }}
        sx={{
          '& .MuiMenu-paper': {
            boxShadow: '-2px 2px 8px 0px rgba(0, 0, 0, 0.10)'
          }
        }}
      >
        {itemList.map(item => (
          <MenuItem disableRipple key={item.route} className="p-0 min-w-[200px]">
            <Link
              target="_self"
              href={getHref(item)}
              className="font-semibold text-lg md:text-xl block text-center flex-1 px-6 py-2"
            >
              {item.name}
            </Link>
          </MenuItem>
        ))}
      </Menu>

      <MarketPageSeo filterBy={filterBy} averageRent={averageRent} />
      <Box className=" min-h-screen">
        <Header isMobileDevice={!isPC} isShowMap={false} showLogo />
        <div className="relative text-primary pt-5 bg-white">
          <div className="hidden lg:block absolute right-0 top-52 bottom-0">
            <Search locationName={locationName} city={cityName} area={areaName} />
          </div>
          <div className="max-w-[820px] px-5 mx-auto">
            <PageBreadcrumbs className="mb-8">
              <div className="text-sm text-primary font-semibold">Market Trends</div>
            </PageBreadcrumbs>

            {/* city selector */}
            <Box
              component="div"
              className="bg-gray-100 flex items-center rounded-xl px-3 py-2 font-semibold text-xl w-fit mb-6 md:mb-8"
            >
              <Box className="flex items-center">
                <MapIcon className="w-5 h-5 text-xl mr-2" />
                <Button
                  id="dropdown-city-selector"
                  aria-controls={openArea ? 'city-area-selector-drop' : undefined}
                  aria-haspopup="true"
                  aria-expanded={openArea ? 'true' : undefined}
                  disableElevation
                  onClick={e => {
                    setDropDownId('dropdown-city-selector')
                    setAnchor(e.currentTarget)
                  }}
                  className="p-0 font-semibold text-lg md:text-xl capitalize"
                  variant="text"
                  endIcon={<ArrowDropDownRoundedIcon />}
                >
                  {cityName}
                </Button>
              </Box>
              {itemList.length ? (
                <>
                  <Box className="w-[2px] h-5 bg-gray-300 mx-5" />
                  <Box className="flex items-center">
                    <Button
                      id="dropdown-area-selector"
                      aria-controls={openArea ? 'city-area-selector-drop' : undefined}
                      aria-haspopup="true"
                      aria-expanded={openArea ? 'true' : undefined}
                      disableElevation
                      onClick={e => {
                        setDropDownId('dropdown-area-selector')
                        setAnchor(e.currentTarget)
                      }}
                      className="p-0 font-semibold text-lg md:text-xl capitalize"
                      variant="text"
                      endIcon={<ArrowDropDownRoundedIcon />}
                    >
                      {areaName || 'All'}
                    </Button>
                  </Box>
                </>
              ) : null}
            </Box>

            {/* header */}
            <Box component="h1" className="font-ramaraja text-4xl md:text-5xl md:leading-[60px]">
              Rental Market Trends {isStudent ? 'near' : 'in'} {locationName}
            </Box>
            <Typography className="mt-4 font-averta text-primary text-base">
              Are you planning a move to {locationName} and unclear about the average rental price
              in the city you&apos;re interested in? Don&apos;t worry, we&apos;ll provide you with
              the market trends of this city. We hope this information helps you find your home of
              choice!
            </Typography>

            {/* average rent */}
            <Box component="h2" className="mt-20 font-ramaraja text-4xl">
              Average Rent {isStudent ? 'near' : 'in'} {locationName}
            </Box>
            <Avg averageRent={averageRent} />

            <AverageRent
              isPC={isPC}
              dataSource={apartmentAverageRent}
              averageRent={averageRent}
              filterBy={filterBy}
              className="mt-6 lg:mt-10"
            />

            <OverTime
              isPC={isPC}
              averageRent={averageRent}
              dataSource={marketTrendsOverTime}
              filterBy={filterBy}
              className="mt-6 lg:mt-10"
            />

            <LineChart medianRentsTrends={medianRentsTrends} filterBy={filterBy} isPC={isPC} />

            <RentByNeighborhood
              rentByNeighborhood={rentByNeighborhood}
              isPC={isPC}
              filterBy={filterBy}
            />

            <div className="mt-15 lg:mt-20 rounded-2xl bg-[#F7F7F7] p-6">
              <div className="text-lg font-bold">
                We ensure the accuracy and transparency of our data
              </div>
              <div className="mt-2 text-sm">
                Our relies on big data and artificial intelligence to summarize and summarize this
                rental data. We combine this data with public record to provide the most up-to-date
                rental information available.
              </div>
            </div>
          </div>
        </div>
      </Box>
      <ToResearch />
      {isMobileDevice ? null : <Footer isMobileDevice={isMobileDevice} />}
      <div className="lg:hidden pt-[140px] lg:pt-0">
        <Search locationName={locationName} city={cityName} area={areaName} />
      </div>
    </>
  )
}

export default MarketTrends
